// Marey / string-diagram chart — hand-rolled SVG.
// Time on X (00:00–24:00 Baghdad), stations on Y, each train a polyline.
import React, { useMemo, useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";

const PAD        = { top: 32, right: 16, bottom: 8, left: 16 };
const PLOT_PAD_V = 24;
const STATION_W  = 184;
const STATION_GAP = 14;
const DAY_MS     = 24 * 3600000;

function fmtTime(ms) {
  const d = new Date(ms);
  return String(d.getUTCHours()).padStart(2, "0") + ":" +
         String(d.getUTCMinutes()).padStart(2, "0");
}

const MareyChart = forwardRef(function MareyChart({
  route,
  trains,
  dayStartMs,
  yDirection,
  theme,
  showNowLine,
  nowMs,
  showDwell,
  highlightTrain,
  setHighlightTrain,
  exportStem,
  compact,
}, ref) {
  const stations = route.stations;
  const N = stations.length;

  const wrapRef       = useRef(null);
  const svgRef        = useRef(null);
  const stateRef      = useRef({});   // stable ref for wheel handler
  const dragAnchor    = useRef(null); // { clientX, viewStart, viewEnd }
  const isDraggingRef = useRef(false);

  const [box,        setBox]        = useState({ w: 0, h: 0 });
  const [viewStart,  setViewStart]  = useState(0);
  const [viewEnd,    setViewEnd]    = useState(DAY_MS);
  const [trackX,     setTrackX]     = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Measure container
  useEffect(() => {
    if (!wrapRef.current) return;
    const measure = () => {
      const r = wrapRef.current.getBoundingClientRect();
      setBox({ w: Math.max(600, r.width), h: Math.max(360, r.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Reset zoom when the selected day changes
  useEffect(() => {
    setViewStart(0);
    setViewEnd(DAY_MS);
  }, [dayStartMs]);

  // Release drag on global mouseup (handles releasing outside SVG)
  useEffect(() => {
    function onUp() {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDragging(false);
      dragAnchor.current = null;
    }
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  // ── Layout math ────────────────────────────────────────────────────────────
  const visibleMs  = viewEnd - viewStart;
  const visibleHrs = visibleMs / 3600000;
  const isZoomed   = visibleMs < DAY_MS - 1000;

  const availW = (box.w || 1100) - PAD.left - PAD.right - STATION_W - STATION_GAP;
  const HOUR_W = Math.max(32, availW / visibleHrs);
  const availH = (box.h || 600) - PAD.top - PAD.bottom - 8;
  // In compact mode: shrink ROW_H to fit all stations in the visible height
  const ROW_H  = compact
    ? Math.max(5, (availH - PLOT_PAD_V * 2) / Math.max(1, N - 1))
    : Math.max(22, Math.min(48, (availH - PLOT_PAD_V * 2) / Math.max(1, N - 1)));

  const plotW  = HOUR_W * visibleHrs;
  const plotH  = ROW_H * Math.max(1, N - 1) + PLOT_PAD_V * 2;
  const totalW = PAD.left + plotW + STATION_GAP + STATION_W + PAD.right;
  const totalH = PAD.top + plotH + PAD.bottom + 8;

  // Station label font size scales down in compact mode
  const stFontSize = Math.min(12, Math.max(7, ROW_H * 0.6));

  // Keep stateRef fresh for the wheel handler (avoids stale closures)
  stateRef.current = { viewStart, viewEnd, visibleMs, plotW };

  const yOf = (idx) =>
    yDirection === "down"
      ? PAD.top + PLOT_PAD_V + idx * ROW_H
      : PAD.top + PLOT_PAD_V + (N - 1 - idx) * ROW_H;

  // ms is absolute epoch ms; maps to SVG x within the visible window
  const xOf = (ms) =>
    PAD.left + ((ms - dayStartMs - viewStart) / visibleMs) * plotW;

  // ── Adaptive time grid ─────────────────────────────────────────────────────
  const gridTicks = useMemo(() => {
    // tickMs: spacing between minor gridlines (ms)
    // majorMs: spacing between labelled gridlines
    const tickMs  = visibleHrs > 12 ? 3600000 : visibleHrs > 4 ? 1800000 : visibleHrs > 1.5 ? 900000 : 300000;
    const majorMs = visibleHrs > 4  ? 3600000 : visibleHrs > 1.5 ? 1800000 : 900000;
    const ticks = [];
    const first = Math.ceil(viewStart / tickMs) * tickMs;
    for (let t = first; t <= viewEnd + 1; t += tickMs) {
      const major = Math.abs(t % majorMs) < 500; // within 500 ms tolerance
      ticks.push({ offset: t, major });
    }
    return ticks;
  }, [viewStart, viewEnd, visibleHrs]);

  // ── Train paths ────────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trainPaths = useMemo(() => {
    if (!trains || !trains.length) return [];
    return trains.map((tr) => {
      const pts = [];
      (tr.segments || []).forEach(([entry, exit, idx]) => {
        pts.push({ x: xOf(entry), y: yOf(idx), ms: entry, station: idx, type: "in"  });
        pts.push({ x: xOf(exit),  y: yOf(idx), ms: exit,  station: idx, type: "out" });
      });
      const d = pts.map((p, k) => (k === 0 ? "M" : "L") + p.x.toFixed(1) + " " + p.y.toFixed(1)).join(" ");
      const plotMaxX = PAD.left + plotW;
      const visible  = pts.filter(p => p.x >= PAD.left && p.x <= plotMaxX);
      const last     = visible[visible.length - 1] || pts[pts.length - 1] || { x: PAD.left, y: PAD.top + PLOT_PAD_V };
      const labelW   = tr.name.length * 6.5 + 10;
      let lx = last.x - labelW - 6;
      if (lx < PAD.left + 2)           lx = last.x + 6;
      if (lx + labelW > plotMaxX - 2)  lx = plotMaxX - labelW - 2;
      return { ...tr, pts, d, label: { x: lx, y: last.y, w: labelW } };
    });
  }, [trains, yDirection, N, plotW, plotH, ROW_H, dayStartMs, viewStart, viewEnd]); // eslint-disable-line

  // ── Wheel zoom (non-passive listener, uses stateRef to avoid stale closure) ─
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    function onWheel(e) {
      const { viewStart: vs, viewEnd: ve, visibleMs: vms, plotW: pw } = stateRef.current;
      const rect = el.getBoundingClientRect();
      const rawX = e.clientX - rect.left;
      // Only hijack the wheel when cursor is inside the time-plot area.
      // Outside (station column, padding) let the browser scroll normally.
      if (rawX < PAD.left || rawX > PAD.left + pw) return;
      e.preventDefault();
      const ratio  = Math.max(0, Math.min(1, (rawX - PAD.left) / pw));
      const pivot  = vs + ratio * vms;
      const factor = e.deltaY > 0 ? 1.3 : 1 / 1.3;
      const newVis = Math.max(30 * 60000, Math.min(DAY_MS, vms * factor));
      let ns = pivot - ratio * newVis;
      let ne = ns + newVis;
      if (ns < 0)      { ns = 0;      ne = newVis; }
      if (ne > DAY_MS) { ne = DAY_MS; ns = ne - newVis; }
      setViewStart(Math.round(ns));
      setViewEnd(Math.round(ne));
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []); // empty — stateRef handles current values

  // ── Mouse handlers ─────────────────────────────────────────────────────────
  function svgCoord(clientX, clientY) {
    const el = svgRef.current;
    if (!el) return null;
    const pt = el.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    return pt.matrixTransform(el.getScreenCTM().inverse());
  }

  function handleMouseDown(e) {
    if (e.button !== 0 || !isZoomed) return;
    const p = svgCoord(e.clientX, e.clientY);
    if (!p || p.x < PAD.left || p.x > PAD.left + plotW) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    setTrackX(null);
    dragAnchor.current = { clientX: e.clientX, viewStart, viewEnd };
    e.preventDefault();
  }

  function handleMouseMove(e) {
    if (isDraggingRef.current && dragAnchor.current) {
      const { clientX: ax, viewStart: as, viewEnd: ae } = dragAnchor.current;
      const pw    = stateRef.current.plotW;
      const range = ae - as;
      const dms   = -((e.clientX - ax) / pw) * range; // right drag = earlier time
      let ns = as + dms, ne = ae + dms;
      if (ns < 0)      { ns = 0;      ne = range; }
      if (ne > DAY_MS) { ne = DAY_MS; ns = ne - range; }
      setViewStart(Math.round(ns));
      setViewEnd(Math.round(ne));
      return;
    }
    const p = svgCoord(e.clientX, e.clientY);
    if (!p || p.x < PAD.left || p.x > PAD.left + plotW ||
               p.y < PAD.top  || p.y > PAD.top  + plotH) {
      setTrackX(null);
      return;
    }
    setTrackX(p.x);
  }

  // ── Track line data ────────────────────────────────────────────────────────
  const cursorMs = trackX != null
    ? dayStartMs + viewStart + ((trackX - PAD.left) / plotW) * visibleMs
    : null;

  function getTrainAtMs(tr, ms) {
    const segs = tr.segments || [];
    for (let i = 0; i < segs.length; i++) {
      const [entry, exit, sIdx] = segs[i];
      if (ms >= entry && ms <= exit)
        return { y: yOf(sIdx), status: "dwell", stationIdx: sIdx };
      if (i + 1 < segs.length) {
        const [nextEntry, , nextSIdx] = segs[i + 1];
        if (ms > exit && ms < nextEntry) {
          const t = (ms - exit) / (nextEntry - exit);
          return { y: yOf(sIdx) + t * (yOf(nextSIdx) - yOf(sIdx)), status: "travel" };
        }
      }
    }
    return null;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackItems = useMemo(() => {
    if (cursorMs == null) return [];
    return trainPaths.map(tr => {
      const info = getTrainAtMs(tr, cursorMs);
      return info ? { name: tr.name, color: tr.color, ...info } : null;
    }).filter(Boolean);
  }, [cursorMs, trainPaths]); // eslint-disable-line

  const TOOLTIP_W     = 190;
  const tooltipOnLeft = trackX != null && (trackX - PAD.left) / plotW > 0.58;
  const tooltipX      = trackX != null ? (tooltipOnLeft ? trackX - TOOLTIP_W - 10 : trackX + 10) : 0;
  const tooltipH      = 34 + trackItems.length * 26;

  // ── Export ────────────────────────────────────────────────────────────────
  async function exportImage() {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const SCALE  = 3;
    const w = svgEl.width.baseVal.value;
    const h = svgEl.height.baseVal.value;

    // Clone and strip ephemeral elements
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns",       "http://www.w3.org/2000/svg");
    clone.setAttribute("xmlns:xhtml", "http://www.w3.org/1999/xhtml");
    const trackEl = clone.querySelector("#irr-track");
    if (trackEl) trackEl.remove();

    // Solid background
    const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bg.setAttribute("width", w); bg.setAttribute("height", h);
    bg.setAttribute("fill", theme.bg);
    clone.insertBefore(bg, clone.firstChild);

    const svgStr  = new XMLSerializer().serializeToString(clone);
    const svgData = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));

    const stem = exportStem || "marey-chart";

    const tryDownload = (href, ext) => {
      const a = document.createElement("a");
      a.download = stem + "." + ext;
      a.href = href;
      a.click();
    };

    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = w * SCALE;
        canvas.height = h * SCALE;
        const ctx = canvas.getContext("2d");
        ctx.scale(SCALE, SCALE);
        ctx.fillStyle = theme.bg;
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          tryDownload(url, "png");
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        }, "image/png");
      } catch (_) {
        // Canvas tainted (foreignObject security) — fall back to SVG vector
        tryDownload(svgData, "svg");
      }
    };
    img.onerror = () => tryDownload(svgData, "svg");
    img.src = svgData;
  }

  useImperativeHandle(ref, () => ({ exportImage })); // eslint-disable-line

  if (!N) return null;

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>

      {/* Zoom controls — lives OUTSIDE the scroll container so it never scrolls away */}
      {isZoomed && (
        <div style={{
          position: "absolute", top: PAD.top + 6, left: PAD.left + 6,
          right: STATION_W + STATION_GAP + PAD.right + 6,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          pointerEvents: "none", zIndex: 10,
        }}>
          {/* Range pill */}
          <div style={{
            pointerEvents: "auto",
            display: "inline-flex", alignItems: "center", gap: 5,
            background: theme.surface2, border: `1px solid ${theme.border}`,
            borderRadius: 5, padding: "3px 8px",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 11, color: theme.textSec, whiteSpace: "nowrap",
            boxShadow: `0 2px 6px ${theme.shadow}`,
          }}>
            <span style={{ fontSize: 10, opacity: .7 }}>⊕</span>
            {fmtTime(dayStartMs + viewStart)}
            <span style={{ color: theme.textMuted, fontSize: 9 }}>—</span>
            {fmtTime(dayStartMs + viewEnd)}
          </div>
          {/* Reset button */}
          <button
            onClick={() => { setViewStart(0); setViewEnd(DAY_MS); }}
            style={{
              pointerEvents: "auto",
              height: 26, padding: "0 12px",
              background: theme.primary, border: `1px solid ${theme.primary}`,
              borderRadius: 5, color: "#fff",
              fontFamily: "Cairo, sans-serif", fontSize: 11, fontWeight: 700,
              cursor: "pointer", direction: "rtl",
              display: "inline-flex", alignItems: "center", gap: 5,
              boxShadow: `0 2px 8px ${theme.shadow}`,
              transition: "opacity .12s, transform .1s",
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = ".82"; e.currentTarget.style.transform = "scale(1.03)"; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = "1";   e.currentTarget.style.transform = "scale(1)"; }}
            onMouseDown={e =>  { e.currentTarget.style.transform = "scale(.97)"; }}
            onMouseUp={e =>    { e.currentTarget.style.transform = "scale(1.03)"; }}
          >
            <span>↺</span>
            عرض اليوم كاملاً
          </button>
        </div>
      )}

      {/* Scrollable SVG area */}
      <div ref={wrapRef} style={{ width: "100%", height: "100%", overflow: compact ? "hidden" : "auto", direction: "ltr" }}>
      <svg
        ref={svgRef}
        width={totalW} height={totalH}
        viewBox={`0 0 ${totalW} ${totalH}`}
        style={{
          display: "block", fontFamily: "Cairo, sans-serif",
          cursor: isDragging ? "grabbing" : isZoomed ? "grab" : "crosshair",
          userSelect: "none",
        }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseLeave={() => { setTrackX(null); }}
      >
        <defs>
          <linearGradient id="nowLineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={theme.signal} stopOpacity="0" />
            <stop offset="6%"   stopColor={theme.signal} stopOpacity="0.9" />
            <stop offset="94%"  stopColor={theme.signal} stopOpacity="0.9" />
            <stop offset="100%" stopColor={theme.signal} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="trackLineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={theme.primary} stopOpacity="0" />
            <stop offset="8%"   stopColor={theme.primary} stopOpacity="0.7" />
            <stop offset="92%"  stopColor={theme.primary} stopOpacity="0.7" />
            <stop offset="100%" stopColor={theme.primary} stopOpacity="0" />
          </linearGradient>
          <clipPath id="plotClip">
            <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} />
          </clipPath>
        </defs>

        {/* Plot background */}
        <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill={theme.plotBg} />

        {/* Station column */}
        <rect
          x={PAD.left + plotW + STATION_GAP} y={PAD.top}
          width={STATION_W} height={plotH}
          fill={theme.surface2} stroke={theme.border} strokeWidth="1" rx="4"
        />
        <foreignObject x={PAD.left + plotW + STATION_GAP} y={PAD.top - 22} width={STATION_W} height={18}>
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            direction: "rtl", display: "flex", alignItems: "center",
            justifyContent: "space-between", height: "100%", padding: "0 12px",
            fontFamily: "Cairo, sans-serif", fontSize: 10, fontWeight: 700,
            color: theme.textMuted, letterSpacing: ".06em",
          }}>
            <span>المحطة</span>
            <span style={{ fontFamily: "JetBrains Mono, monospace" }}>كم</span>
          </div>
        </foreignObject>

        {/* Adaptive time grid */}
        {gridTicks.map(({ offset: t, major }, i) => {
          const x = xOf(dayStartMs + t);
          return (
            <g key={"g" + i}>
              <line
                x1={x} x2={x} y1={PAD.top} y2={PAD.top + plotH}
                stroke={major ? theme.grid : theme.gridSoft}
                strokeWidth="1" strokeDasharray={major ? "" : "2 3"}
              />
              {major && t < DAY_MS && (
                <text x={x + 4} y={PAD.top - 10}
                  fill={theme.textSec} fontSize="10"
                  fontFamily="JetBrains Mono, monospace" textAnchor="start"
                >
                  {fmtTime(dayStartMs + t)}
                </text>
              )}
            </g>
          );
        })}

        {/* Station rows */}
        {stations.map((st, i) => {
          const y     = yOf(i);
          const major = i === 0 || i === N - 1;
          return (
            <g key={"s" + i}>
              <line
                x1={PAD.left} x2={PAD.left + plotW} y1={y} y2={y}
                stroke={major ? theme.grid : theme.gridSoft} strokeWidth="1"
              />
              <foreignObject
                x={PAD.left + plotW + STATION_GAP} y={y - ROW_H / 2 + 1}
                width={STATION_W} height={ROW_H - 2}
              >
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                  direction: "rtl", display: "flex", alignItems: "center",
                  justifyContent: "space-between", height: "100%", padding: "0 12px",
                  fontFamily: "Cairo, sans-serif", fontSize: stFontSize,
                  fontWeight: major ? 700 : 500, color: theme.text,
                  whiteSpace: "nowrap", overflow: "hidden",
                }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>
                    {st.name}
                  </span>
                  <span style={{
                    fontFamily: "JetBrains Mono, monospace", fontSize: 10,
                    color: theme.textMuted, marginInlineStart: 8, flexShrink: 0,
                  }}>{st.km}</span>
                </div>
              </foreignObject>
              <circle cx={PAD.left + plotW} cy={y} r="2.5" fill={theme.text} />
              <line
                x1={PAD.left + plotW + 2.5} x2={PAD.left + plotW + STATION_GAP - 1}
                y1={y} y2={y} stroke={theme.gridSoft} strokeWidth="1"
              />
            </g>
          );
        })}

        {/* Plot border */}
        <rect
          x={PAD.left} y={PAD.top} width={plotW} height={plotH}
          fill="none" stroke={theme.border} strokeWidth="1"
        />

        {/* Now line */}
        {showNowLine && nowMs >= dayStartMs + viewStart && nowMs <= dayStartMs + viewEnd && (
          <g>
            <line
              x1={xOf(nowMs)} x2={xOf(nowMs)}
              y1={PAD.top - 4} y2={PAD.top + plotH + 4}
              stroke="url(#nowLineGrad)" strokeWidth="1.5"
            />
          </g>
        )}

        {/* Trains — clipped to plot area */}
        <g clipPath="url(#plotClip)">
          {trainPaths.map((tr, i) => {
            const dim = highlightTrain != null && highlightTrain !== i;
            return (
              <g key={"t" + i} opacity={dim ? 0.18 : 1} style={{ transition: "opacity 160ms" }}>
                {showDwell && (tr.segments || []).map(([a, b, sIdx], k) => (
                  <line key={"d" + k}
                    x1={xOf(a)} x2={xOf(b)} y1={yOf(sIdx)} y2={yOf(sIdx)}
                    stroke={tr.color} strokeWidth="6" strokeLinecap="round" opacity="0.32"
                  />
                ))}
                <path
                  d={tr.d} fill="none" stroke={tr.color}
                  strokeWidth={highlightTrain === i ? 2.6 : 1.8}
                  strokeLinejoin="round" strokeLinecap="round"
                  onMouseEnter={() => setHighlightTrain(i)}
                  onMouseLeave={() => setHighlightTrain(null)}
                  style={{ cursor: isDragging ? "grabbing" : "pointer" }}
                />
                {tr.pts.filter(p => p.type === "in").map((p, k) => (
                  <circle key={"p" + k} cx={p.x} cy={p.y} r="2"
                    fill={theme.plotBg} stroke={tr.color} strokeWidth="1.2"
                  />
                ))}
                {tr.label && tr.pts.length > 0 && (
                  <g transform={`translate(${tr.label.x}, ${tr.label.y - 9})`}>
                    <rect x="0" y="-9" width={tr.label.w} height="14" rx="3" fill={tr.color} />
                    <text x={tr.label.w / 2} y="1" fontSize="10"
                      fontFamily="JetBrains Mono, monospace"
                      fill="#fff" fontWeight="700" textAnchor="middle">
                      {tr.name}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </g>


        {/* ── Track line (hidden while panning) ──────────────────────────────── */}
        {!isDragging && trackX != null && (
          <g id="irr-track" style={{ pointerEvents: "none" }}>
            <line
              x1={trackX} x2={trackX} y1={PAD.top} y2={PAD.top + plotH}
              stroke="url(#trackLineGrad)" strokeWidth="1.2"
            />
            <rect
              x={trackX - 1} y={PAD.top} width={2} height={plotH}
              fill={theme.primary} opacity="0.06"
            />
            {/* Time badge at bottom of line */}
            <g transform={`translate(${trackX}, ${PAD.top + plotH + 10})`}>
              <rect x="-24" y="-10" width="48" height="15" rx="3" fill={theme.primary} opacity="0.92" />
              <text x="0" y="1" textAnchor="middle" fontSize="9.5"
                fontFamily="JetBrains Mono, monospace" fill="#fff" fontWeight="700">
                {fmtTime(cursorMs)}
              </text>
            </g>
            {/* Intersection dots */}
            {trackItems.map((item, i) => (
              <circle key={"td" + i}
                cx={trackX} cy={item.y} r="4"
                fill={item.color} stroke={theme.surface} strokeWidth="1.8"
              />
            ))}
            {/* Tooltip panel */}
            {trackItems.length > 0 && (
              <foreignObject
                x={tooltipX} y={PAD.top + PLOT_PAD_V}
                width={TOOLTIP_W} height={tooltipH}
                style={{ overflow: "visible" }}
              >
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 7,
                  boxShadow: `0 6px 20px ${theme.shadow}`,
                  padding: "9px 11px 7px",
                  direction: "rtl",
                  fontFamily: "Cairo, sans-serif",
                  width: TOOLTIP_W + "px",
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 7,
                    borderBottom: `1px solid ${theme.borderS}`,
                    paddingBottom: 6, marginBottom: 5,
                  }}>
                    <span style={{
                      fontFamily: "JetBrains Mono, monospace",
                      fontSize: 14, fontWeight: 700,
                      color: theme.primary, letterSpacing: ".04em",
                    }}>{fmtTime(cursorMs)}</span>
                    <span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 600 }}>
                      توقيت بغداد
                    </span>
                  </div>
                  {trackItems.map((item, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "3px 0",
                      borderBottom: i < trackItems.length - 1 ? `1px solid ${theme.borderS}` : "none",
                    }}>
                      <span style={{
                        width: 9, height: 9, borderRadius: 2,
                        background: item.color, flexShrink: 0,
                      }} />
                      <span style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10.5, fontWeight: 700,
                        color: theme.text, flexShrink: 0, minWidth: 68,
                      }}>{item.name}</span>
                      <span style={{
                        fontSize: 11,
                        color: item.status === "dwell" ? theme.textSec : theme.textMuted,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
                        fontStyle: item.status === "travel" ? "italic" : "normal",
                      }}>
                        {item.status === "dwell"
                          ? (stations[item.stationIdx] && stations[item.stationIdx].name) || ""
                          : "في الطريق"}
                      </span>
                    </div>
                  ))}
                </div>
              </foreignObject>
            )}
          </g>
        )}
      </svg>
      </div>  {/* end scroll container */}
    </div>
  );
});

export default MareyChart;
